import React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export type ErrorBoundaryLevel = 'app' | 'page' | 'widget';

interface ErrorBoundaryProps {
  level: ErrorBoundaryLevel;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly errorContainerRef = React.createRef<HTMLDivElement>();

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report to Sentry (no-ops if Sentry is not initialized)
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    this.props.onError?.(error, errorInfo);
    // Move focus to the error container for screen reader announcement
    this.errorContainerRef.current?.focus();
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    switch (this.props.level) {
      case 'app':
        return <AppFallback onReload={this.handleReload} containerRef={this.errorContainerRef} />;
      case 'page':
        return <PageFallback onRetry={this.handleReset} containerRef={this.errorContainerRef} />;
      case 'widget':
        return <WidgetFallback onRetry={this.handleReset} containerRef={this.errorContainerRef} />;
    }
  }
}

function AppFallback({ onReload, containerRef }: Readonly<{ onReload: () => void; containerRef: React.RefObject<HTMLDivElement | null> }>) {
  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="alert"
      aria-live="assertive"
      className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 p-6 focus:outline-none"
    >
      <AlertTriangle aria-hidden="true" className="h-16 w-16 text-red-500 mb-6" />
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
        Something went wrong
      </h1>
      <p className="text-slate-600 dark:text-slate-400 mb-6 text-center max-w-md">
        An unexpected error occurred. Please reload the page to continue.
      </p>
      <Button onClick={onReload}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Reload Page
      </Button>
    </div>
  );
}

function PageFallback({ onRetry, containerRef }: Readonly<{ onRetry: () => void; containerRef: React.RefObject<HTMLDivElement | null> }>) {
  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="alert"
      aria-live="assertive"
      className="flex flex-col items-center justify-center py-16 px-6 focus:outline-none"
    >
      <AlertTriangle aria-hidden="true" className="h-12 w-12 text-red-500 mb-4" />
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
        Something went wrong
      </h2>
      <p className="text-slate-600 dark:text-slate-400 mb-4 text-center max-w-sm">
        This page encountered an error. You can try again or navigate elsewhere.
      </p>
      <Button variant="outline" onClick={onRetry}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}

function WidgetFallback({ onRetry, containerRef }: Readonly<{ onRetry: () => void; containerRef: React.RefObject<HTMLDivElement | null> }>) {
  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="alert"
      aria-live="assertive"
      className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm focus:outline-none"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
      <span>Failed to load</span>
      <button
        type="button"
        onClick={onRetry}
        aria-label="Retry loading this component"
        className="ml-auto text-red-600 dark:text-red-400 underline hover:no-underline text-sm"
      >
        Retry
      </button>
    </div>
  );
}

export default ErrorBoundary;
